/**
 * ReactionBar - 👍 / 👎 per-message or per-session reaction with optional comment.
 *
 * Controlled component. Parent supplies the current reaction state and a
 * handler created by lib/feedback.ts. Retro mission-control palette: amber for
 * up, red for down; dim when not selected. Color is data, never decoration.
 */

import { useCallback, useMemo, useState } from 'react'
import { ThumbsUp, ThumbsDown, MessageSquare, X } from 'lucide-react'
import { FEEDBACK_COMMENT_MAX, FeedbackRating, FeedbackReaction } from '../../types/feedback'
import type { ReactionHandler } from '../../lib/feedback'

interface ReactionBarProps {
  reaction: FeedbackReaction | null
  handler: ReactionHandler
  variant?: 'message' | 'session'
  testId?: string
}

function counterColor(length: number): string {
  if (length >= 9000) return '#ff6666' // red
  if (length >= 1500) return '#ffcc66' // amber
  return '#888888' // dim
}

function ReactionBar({ reaction, handler, variant = 'message', testId }: ReactionBarProps) {
  const [commentOpen, setCommentOpen] = useState(false)
  const [draft, setDraft] = useState<string>('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const activeRating: FeedbackRating | null = reaction?.rating ?? null
  const hasComment = Boolean(reaction?.comment)

  const upColor = activeRating === 'up' ? '#ffcc66' : '#555555'
  const downColor = activeRating === 'down' ? '#ff6666' : '#555555'

  const draftLength = draft.length
  const overLimit = draftLength > FEEDBACK_COMMENT_MAX

  const sizeClass = variant === 'session' ? 'w-4 h-4' : 'w-3.5 h-3.5'
  const wrapClass =
    variant === 'session'
      ? 'flex items-center gap-2 text-xs'
      : 'flex items-center gap-2 mt-1 text-xs opacity-80 hover:opacity-100 transition-opacity'

  const wrappedToggle = useCallback(
    async (rating: FeedbackRating) => {
      if (busy) return
      setBusy(true)
      setError(null)
      try {
        await handler.toggle(rating)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Submit failed')
      } finally {
        setBusy(false)
      }
    },
    [busy, handler],
  )

  const submitComment = useCallback(async () => {
    if (busy) return
    if (overLimit) return
    setBusy(true)
    setError(null)
    try {
      await handler.setComment(draft)
      setCommentOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submit failed')
    } finally {
      setBusy(false)
    }
  }, [busy, draft, handler, overLimit])

  const openComment = useCallback(() => {
    setDraft(reaction?.comment ?? '')
    setCommentOpen(true)
  }, [reaction?.comment])

  const counterColorValue = useMemo(() => counterColor(draftLength), [draftLength])

  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <div className={wrapClass}>
        <button
          type="button"
          aria-label="Thumbs up"
          aria-pressed={activeRating === 'up'}
          data-testid="reaction-up"
          disabled={busy}
          onClick={() => wrappedToggle('up')}
          className="p-1 rounded hover:bg-retro-surface transition-colors disabled:opacity-50"
        >
          <ThumbsUp className={sizeClass} style={{ color: upColor }} />
        </button>
        <button
          type="button"
          aria-label="Thumbs down"
          aria-pressed={activeRating === 'down'}
          data-testid="reaction-down"
          disabled={busy}
          onClick={() => wrappedToggle('down')}
          className="p-1 rounded hover:bg-retro-surface transition-colors disabled:opacity-50"
        >
          <ThumbsDown className={sizeClass} style={{ color: downColor }} />
        </button>
        {activeRating !== null && (
          <button
            type="button"
            aria-label={hasComment ? 'Edit comment' : 'Add comment'}
            data-testid="reaction-comment-toggle"
            disabled={busy}
            onClick={() => (commentOpen ? setCommentOpen(false) : openComment())}
            className="p-1 rounded hover:bg-retro-surface transition-colors text-retro-dim hover:text-retro-amber disabled:opacity-50"
          >
            <MessageSquare className={sizeClass} />
          </button>
        )}
        {hasComment && !commentOpen && (
          <span className="text-retro-dim font-mono truncate max-w-[24ch]">
            "{reaction?.comment}"
          </span>
        )}
      </div>

      {commentOpen && (
        <div className="flex flex-col gap-1 mt-1 ml-1 w-full max-w-md">
          <textarea
            data-testid="reaction-comment-textarea"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add a comment (optional)"
            rows={3}
            className="w-full bg-retro-surface border border-retro-border rounded px-2 py-1 text-xs font-mono text-dark-600 focus:outline-none focus:border-retro-amber"
          />
          <div className="flex items-center justify-between text-[10px] font-mono">
            <span style={{ color: counterColorValue }}>
              {draftLength.toLocaleString()} / {FEEDBACK_COMMENT_MAX.toLocaleString()}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCommentOpen(false)}
                className="px-2 py-0.5 rounded border border-retro-border text-retro-dim hover:text-dark-600"
              >
                <X className="w-3 h-3" />
              </button>
              <button
                type="button"
                data-testid="reaction-comment-submit"
                onClick={submitComment}
                disabled={overLimit || busy}
                className="px-2 py-0.5 rounded border border-retro-amber text-retro-amber hover:bg-retro-amber/10 disabled:opacity-40"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <span className="text-[10px] font-mono" style={{ color: '#ff6666' }}>
          {error}
        </span>
      )}
    </div>
  )
}

export default ReactionBar
